// Number and date formatting. Every formatter takes unknown and returns "—"
// for missing or non-numeric input, so callers can pass API fields straight in.

export const NA = "—";

export function num(v: unknown): number | undefined {
  return typeof v === "number" && isFinite(v) ? v : undefined;
}

function fixed(v: number, dp: number): string {
  return v.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

/// Price with precision that suits its magnitude, or `dp` if given.
export function price(v: unknown, dp?: number): string {
  const n = num(v);
  if (n === undefined) return NA;
  if (dp !== undefined) return fixed(n, dp);
  const a = Math.abs(n);
  return fixed(n, a >= 1 ? 2 : a >= 0.01 ? 4 : 6);
}

export function fixedN(v: unknown, dp = 2): string {
  const n = num(v);
  return n === undefined ? NA : fixed(n, dp);
}

export function chg(v: unknown, dp?: number): string {
  const n = num(v);
  if (n === undefined) return NA;
  const a = Math.abs(n);
  return (n > 0 ? "+" : "") + fixed(n, dp ?? (a >= 0.01 || a === 0 ? 2 : a >= 0.0001 ? 4 : 6));
}

/// Percent from a value already in percent units (Yahoo's *ChangePercent).
export function pct(v: unknown, dp = 2): string {
  const n = num(v);
  if (n === undefined) return NA;
  return (n > 0 ? "+" : "") + fixed(n, dp) + "%";
}

/// Percent from a ratio (0.0123 → 1.23%), unsigned.
export function ratio(v: unknown, dp = 2): string {
  const n = num(v);
  return n === undefined ? NA : fixed(n * 100, dp) + "%";
}

/// 1.23T, 45.6B, 7.89M, 12.3K.
export function big(v: unknown, dp = 2): string {
  const n = num(v);
  if (n === undefined) return NA;
  const a = Math.abs(n);
  const units: [number, string][] = [
    [1e12, "T"],
    [1e9, "B"],
    [1e6, "M"],
    [1e3, "K"],
  ];
  for (const [d, u] of units) if (a >= d) return fixed(n / d, dp) + u;
  return fixed(n, a < 10 && n % 1 !== 0 ? 2 : 0);
}

export function int(v: unknown): string {
  const n = num(v);
  return n === undefined ? NA : Math.round(n).toLocaleString("en-US");
}

/// Basis points from a difference in percent units.
export function bp(v: unknown): string {
  const n = num(v);
  if (n === undefined) return NA;
  const b = Math.round(n * 100);
  return (b > 0 ? "+" : "") + b + "bp";
}

/// "up" / "down" / "" class for a signed value.
export function cls(v: unknown): string {
  const n = num(v);
  return n === undefined || n === 0 ? "" : n > 0 ? "up" : "down";
}

function toDate(v: unknown): Date | undefined {
  if (v instanceof Date) return isNaN(v.getTime()) ? undefined : v;
  if (typeof v === "number" && isFinite(v)) return new Date(v < 1e11 ? v * 1000 : v);
  if (typeof v === "string" && v) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

const pad = (n: number) => String(n).padStart(2, "0");

/// YYYY-MM-DD in local time. Accepts unix seconds, millis or a date string.
export function date(v: unknown): string {
  const d = toDate(v);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : NA;
}

/// YYYY-MM-DD of a UTC timestamp, for dates that are calendar days (fiscal
/// period ends, dividend dates) rather than instants.
export function utcDate(v: unknown): string {
  const d = toDate(v);
  return d ? `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` : NA;
}

export function time(v: unknown, seconds = false): string {
  const d = toDate(v);
  if (!d) return NA;
  const t = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return seconds ? `${t}:${pad(d.getSeconds())}` : t;
}

/// Time today, otherwise date: how terminals stamp quotes and headlines.
export function stamp(v: unknown): string {
  const d = toDate(v);
  if (!d) return NA;
  const now = new Date();
  return d.toDateString() === now.toDateString() ? time(d) : date(d);
}

const DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/// Local calendar day as a heading: "FRI 16 OCT · TODAY", "THU 15 OCT".
export function day(v: unknown): string {
  const d = toDate(v);
  if (!d) return NA;
  const label = `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
  const offset = (new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86_400_000;
  const rel = { [-1]: "YESTERDAY", 0: "TODAY", 1: "TOMORROW" }[Math.round(offset)];
  return rel ? `${label} · ${rel}` : label;
}

/// "3m ago", "2h ago", "5d ago".
export function ago(v: unknown): string {
  const d = toDate(v);
  if (!d) return NA;
  const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
